import { type ClassValue, clsx } from "clsx";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import UAParser from "ua-parser-js";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const formatNumber = (number: number, round?: boolean) => {
    if (round) {
        number = Math.round(number);
    }
    if (number >= 1000000) {
        return (number / 1000000).toLocaleString() + "M";
    } else if (number >= 1000) {
        return (number / 1000).toLocaleString() + "k";
    } else {
        return number.toLocaleString();
    }
};
export const formatMeals = (meals: (string | boolean)[]) => {
    if (meals.length === 0) return "No Meals";
    if (meals.length === 1) return "Only " + meals[0];
    if (meals.length === 2) return meals[0] + " & " + meals[1];
    if (meals.length === 3) return meals[0] + ", " + meals[1] + " & " + meals[2];
};

export const copyToClipboard = (text: string, title?: string) => {
    navigator.clipboard.writeText(text);
    const message = title ? `${title} copied to clipboard` : "Copied to clipboard";
    toast.success(message);
};

export const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export const formatFromCamelCase = (text: string) => {
    if (text === text.toUpperCase()) {
        return text;
    } else {
        // Split camelCase words and capitalize
        return text
            .split(/(?=[A-Z])/)
            .map(capitalize)
            .join(" ");
    }
};

export const formatUserAgent = (userAgent: string) => {
    const parsedUA = new UAParser(userAgent);
    return `${parsedUA.getDevice().model ? parsedUA.getDevice().model + " " + "/ " : ""}${parsedUA.getOS().name} ${parsedUA.getOS().version}`;
};

export function formatDate(date: Date | string | number, opts: Intl.DateTimeFormatOptions = {}) {
    const locale = localStorage.getItem("locale") || "en-US";
    return new Intl.DateTimeFormat(locale === "fr" ? "fr" : "en-US", {
        month: opts.month ?? "long",
        day: opts.day ?? "numeric",
        year: opts.year ?? "numeric",
        ...opts,
    }).format(new Date(date));
}

export function formatRelativeTime(date: Date | string | number): string {
    const now = new Date();
    const then = new Date(date);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    if (seconds < 60) {
        return seconds === 1 ? "1 second ago" : `${seconds} seconds ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
        return days === 1 ? "1 day ago" : `${days} days ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
        return months === 1 ? "1 month ago" : `${months} months ago`;
    }

    const years = Math.floor(months / 12);
    return years === 1 ? "1 year ago" : `${years} years ago`;
}

export function toSentenceCase(str: string) {
    return str
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * @see https://github.com/radix-ui/primitives/blob/main/packages/core/primitive/src/primitive.tsx
 */
export function composeEventHandlers<E>(
    originalEventHandler?: (event: E) => void,
    ourEventHandler?: (event: E) => void,
    { checkForDefaultPrevented = true } = {}
) {
    return function handleEvent(event: E) {
        originalEventHandler?.(event);

        if (checkForDefaultPrevented === false || !(event as unknown as Event).defaultPrevented) {
            return ourEventHandler?.(event);
        }
    };
}

export function addDays(startDate: Date, n: number): Date {
    // Create a new Date object to avoid mutating the original date
    const result = new Date(startDate);
    result.setDate(result.getDate() + n);
    return result;
}

export const getCookie = (name: string) => {
    const cookieString = document.cookie; // e.g., "user=John; sessionId=abc123"
    const cookies = cookieString.split("; "); // Split into individual cookies
    for (const cookie of cookies) {
      const [key, value] = cookie.split("="); // Split into key-value pair
      if (key === name) {
        return decodeURIComponent(value); // Return decoded value if name matches
      }
    }
    return null; // Return null if cookie not found
};